import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { NAVY, PageHeader, headCell, bodyCell } from '../components/widgets.comp';
import { ScoringBandCards, RiskRubricTable } from '../../DashboardCommon/AssessmentScoring.comp';
import { DEPLOYMENT_ACTION_BAND_COLORS } from '../../../utils/constants';

import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import PeopleIcon from '@material-ui/icons/People';
import StorageIcon from '@material-ui/icons/Storage';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';

const METADATA = {
    'A': { icon: VerifiedUserIcon, color: '#1B6B55' },
    'B': { icon: ReportProblemIcon, color: '#E04535' },
    'C': { icon: PeopleIcon, color: '#3B6FA0' },
    'D': { icon: StorageIcon, color: '#4B5563' },
    'E': { icon: TrendingUpIcon, color: '#0D9488' }
};

const getBandColor = (name) => {
    const trimmed = name.trim();
    if (DEPLOYMENT_ACTION_BAND_COLORS[trimmed]) return DEPLOYMENT_ACTION_BAND_COLORS[trimmed];

    const clean = trimmed.toUpperCase();
    if (clean === 'HIGH' || clean === 'STRONG ENABLING CONTEXT') return '#1B6B55';
    if (clean === 'MODERATE' || clean === 'MONITOR' || clean === 'ADEQUATE ENABLING CONTEXT') return '#4A9278';
    if (clean === 'BASIC' || clean === 'WATCH' || clean === 'LIMITED ENABLING CONTEXT' || clean === 'MEDIUM') return '#EFA020';
    if (clean === 'ELEVATED' || clean === 'LOW') return '#E8730C';
    if (clean === 'LOW ENABLING CONTEXT' || clean === 'PRIORITY CONCERN') return '#E04535';
    if (clean === 'VERY LOW') return '#9CA3AF';
    return '#6B7280';
};

const Chip = ({ text, color }) => (
    <Box sx={{
        backgroundColor: color, color: '#ffffff', fontSize: '12px', fontWeight: 700,
        px: 1.5, py: 0.5, borderRadius: '6px', whiteSpace: 'nowrap',
    }}>
        {text}
    </Box>
);

const parseHeadlineHtml = (html) => {
    // Extract description from the first <p>...</p>
    const pMatch = html.match(/<p>(.*?)<\/p>/i);
    const desc = pMatch ? pMatch[1].replace(/<strong>.*?<\/strong>/gi, '').trim() : '';

    // Extract table rows
    const bands = [];
    const trRegex = /<tr>\s*<td>(.*?)<\/td>\s*<td>(.*?)<\/td>\s*<\/tr>/gi;
    let match;
    while ((match = trRegex.exec(html)) !== null) {
        bands.push({
            name: match[1].trim(),
            meaning: match[2].trim()
        });
    }

    return { desc, bands };
};

export const RubricCard = ({ card }) => {
    const meta = METADATA[card.headline_code] || METADATA['D'];
    const Icon = meta.icon;
    const { desc, bands } = parseHeadlineHtml(card.headline_html || '');

    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', backgroundColor: `${meta.color}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <Icon style={{ color: meta.color, fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY, lineHeight: 1.3 }}>
                    {card.headline_title}
                </Typography>
            </Box>
            {desc && (
                <Typography sx={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6, mb: 2 }}>
                    {desc}
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {bands.map((b) => (
                    <Box key={b.name}>
                        <Box sx={{ display: 'inline-flex', mb: 0.6 }}>
                            <Chip text={b.name} color={getBandColor(b.name)} />
                        </Box>
                        <Typography sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5 }}>
                            {b.meaning}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export const ThresholdTable = ({ card, listCard }) => {
    let tableObjects = card?.table_objects || [];
    
    if (listCard && listCard.list_objects) {
        const riskCodeCol = tableObjects.find(col => (col.column_name || '').toLowerCase() === 'risk code');
        if (riskCodeCol) {
            const codesInTable = riskCodeCol.values || [];
            const hashValues = [];
            const categoryValues = [];
            const indicatorValues = [];
            
            const otherCols = tableObjects.filter(col => {
                const name = (col.column_name || '').toLowerCase();
                return name !== 'risk code' && name !== 'item';
            });
            
            const otherColsNewValues = otherCols.map(() => []);
            
            listCard.list_objects.forEach(item => {
                const code = item.code;
                const matchedIndex = codesInTable.indexOf(code);
                if (matchedIndex !== -1) {
                    categoryValues.push(item.parent_name || '');
                    indicatorValues.push(item.name || '');
                    const subname = item.subname || '';
                    const numMatch = subname.match(/\d+/);
                    hashValues.push(numMatch ? numMatch[0] : '');
                    
                    otherCols.forEach((col, idx) => {
                        otherColsNewValues[idx].push(col.values[matchedIndex] || '');
                    });
                }
            });
            
            tableObjects = [
                { column_name: '#', values: hashValues },
                { column_name: 'Risk Category', values: categoryValues },
                { column_name: 'Risk indicator', values: indicatorValues },
                ...otherCols.map((col, idx) => ({
                    ...col,
                    values: otherColsNewValues[idx]
                }))
            ];
        }
    }

    const title = card?.title || 'CPCI Child Cyber Risk Rubric Threshold';
    const rowCount = tableObjects[0]?.values?.length || 0;

    const hCell = { ...headCell, py: 2 };
    const bCell = { ...bodyCell, py: 2 };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 800, color: NAVY, mb: 2 }}>{title}</Typography>
            <TableContainer sx={{ border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {tableObjects.map((col, idx) => (
                                <TableCell key={idx} sx={{
                                    ...hCell,
                                    textAlign: idx < 3 ? 'left' : 'center',
                                    width: idx === 0 ? '5%' : idx === 1 ? '15%' : idx === 2 ? '25%' : idx === 3 ? '15%' : '10%'
                                }}>
                                    {col.column_name}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.from({ length: rowCount }).map((_, rowIndex) => (
                            <TableRow key={rowIndex} sx={{ backgroundColor: rowIndex % 2 === 0 ? 'var(--brand-surface, #fff)' : 'var(--brand-surface-subtle, #F9FAFB)' }}>
                                {tableObjects.map((col, colIndex) => {
                                    const val = col.values[rowIndex];
                                    const isIndicator = colIndex === 2;
                                    return (
                                        <TableCell key={colIndex} sx={{
                                            ...bCell,
                                            textAlign: colIndex < 3 ? 'left' : 'center',
                                            fontWeight: isIndicator ? 700 : 'normal',
                                            color: isIndicator ? NAVY : '#374151'
                                        }}>
                                            {val}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

/*
 * SD Methodology — "How the COS Assessment Is Scored". A common slide, so it is
 * static front-end text from Ian's Common Section deck (p.16–17), shared with
 * the Network dashboard (see DashboardCommon/AssessmentScoring). It used to read
 * S_16, which the datahouse no longer sends, so the page rendered empty.
 * RubricCard / ThresholdTable above stay exported for the National dashboard.
 */
const AboutRubricsTab = () => (
    <Box sx={{ pb: 4 }}>
        <PageHeader
            eyebrow=""
            title="How the COS Assessment Is Scored"
            subtitle="What was measured, and how should it be interpreted?"
        />
        <ScoringBandCards />
        <RiskRubricTable />
    </Box>
);

export default AboutRubricsTab;
